interface Props {
  error: Error;
  onReset: () => void;
}

/**
 * Global error fallback — the SPA's 500-equivalent.
 *
 * Renders when `ErrorBoundary.componentDidCatch` fires (any uncaught render
 * error or thrown promise rejection bubbled to the root). Since this is a
 * Vite SPA with no SSR there is no HTTP 500 surface; this page is the user
 * experience for "something blew up on the client" — the same role Albert's
 * 2026-05-20 review names "Dedicated 500 error page."
 *
 * Implementation is deliberately defensive — raw HTML + brand-token Tailwind
 * utility classes, NOT the wrapped brand primitives. If the design system
 * itself is the thing that broke (which is why ErrorBoundary fired),
 * rendering `<Button>` here would loop the error.
 *
 * 404 is handled separately by `src/pages/_errors/NotFoundPage.tsx` via
 * the React Router catch-all route.
 */
export default function ErrorPage({ error, onReset }: Props) {
  const isDev = import.meta.env.DEV;

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 p-8">
      <h1 className="text-h2 text-grey-900">Something went wrong</h1>
      <p className="max-w-md text-center text-body text-grey-700">
        The app hit an unexpected error and stopped responding. Try again, or head back to the
        screening portal. If this keeps happening, please contact support — the error has been
        logged.
      </p>
      {isDev && (
        <details className="max-w-2xl">
          <summary className="cursor-pointer text-small text-grey-600">
            Error details (dev only)
          </summary>
          <pre className="mt-2 overflow-auto rounded-2xl bg-grey-50 p-4 text-small text-grey-700">
            {error.stack || error.message}
          </pre>
        </details>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onReset}
          className="rounded-2xl bg-green-700 px-4 py-2 text-white"
        >
          Try again
        </button>
        <a
          href="/screening-portal"
          className="rounded-2xl border border-grey-200 px-4 py-2 text-grey-900"
        >
          Back to screening
        </a>
      </div>
    </div>
  );
}
